import { h, defineComponent } from 'vue'
import { createRouter, createWebHashHistory } from 'vue-router'
import AdaptiveView from '@/views/AdaptiveView.vue'
import { setupRouterGuard } from './guard'

// 桌面端组件
const JobList = () => import('@/views/JobList.vue')
const CategoryChart = () => import('@/views/CategoryChart.vue')
const Analysis = () => import('@/views/Analysis.vue')
const Tickets = () => import('@/views/Tickets.vue')
const StockCase = () => import('@/views/StockCase.vue')
const Screen = () => import('@/views/Screen.vue')
const companyScreen = () => import('@/views/companyScreen.vue')
const StockAction = () => import('@/views/StockAction.vue')
const StockArticle = () => import('@/views/StockArticle.vue')
const StockBarChart = () => import('@/views/StockBarChart.vue')
const StockCompounding = () => import('@/views/StockCompounding.vue')
const StockFortune = () => import('@/views/StockFortune.vue')

// 移动端组件
const MobileTickets = () => import('@/views/mobile/MobileTickets.vue')
const MobileStockCase = () => import('@/views/mobile/MobileStockCase.vue')
const MobileStockAction = () => import('@/views/mobile/MobileStockAction.vue')
const MobileStockArticle = () => import('@/views/mobile/MobileStockArticle.vue')
const MobileStockCompounding = () => import('@/views/mobile/MobileStockCompounding.vue')
const MobileStockFortune = () => import('@/views/mobile/MobileStockFortune.vue')
const MobileBarChart = () => import('@/views/mobile/MobileBarChart.vue')
const MobileScreenHint = () => import('@/views/mobile/MobileScreenHint.vue')

/**
 * 生成「桌面端 / 移动端」自适应路由组件
 * 大屏页面在移动端改为提示页，避免 1920 缩放后无法操作
 */
function adaptive(name, desktop, mobile) {
  // 保留 name 以兼容 App.vue 中 keep-alive 的 include 匹配
  return defineComponent({
    name,
    setup() {
      return () => h(AdaptiveView, { desktop, mobile })
    }
  })
}

const routes = [
  // ==============================
  // 登录
  // ==============================
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/login/LoginView.vue'),
    meta: {
      title: '登录',
      hideLayout: true,
      public: true
    }
  },

  // ==============================
  // 系统管理
  // ==============================
  {
    path: '/system/user',
    name: 'UserManage',
    component: () => import('@/views/system/UserManage.vue'),
    meta: { title: '用户管理', permission: 'sys:user:list' }
  },
  {
    path: '/system/role',
    name: 'RoleManage',
    component: () => import('@/views/system/RoleManage.vue'),
    meta: { title: '角色管理', permission: 'sys:role:list' }
  },
  {
    path: '/system/menu',
    name: 'MenuManage',
    component: () => import('@/views/system/MenuManage.vue'),
    meta: { title: '菜单管理', permission: 'sys:menu:list' }
  },
  {
    path: '/system/dept',
    name: 'DeptManage',
    component: () => import('@/views/system/DeptManage.vue'),
    meta: { title: '部门管理', permission: 'sys:dept:list' }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/views/system/ProfileView.vue'),
    meta: { title: '个人信息' }
  },

  // ==============================
  // 错误页
  // ==============================
  {
    path: '/403',
    name: 'Forbidden',
    component: () => import('@/views/error/ForbiddenView.vue'),
    meta: { title: '无权访问' }
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('@/views/error/NotFoundView.vue'),
    meta: { title: '页面不存在', hideLayout: true, public: true }
  },

  {
    path: '/joblist',
    name: 'JobList',
    component: JobList,
    meta: { title: '岗位列表' }
  },
  {
    path: '/category',
    name: 'CategoryChart',
    component: CategoryChart,
    meta: { title: '分类统计' }
  },
  {
    path: '/analysis',
    name: 'Analysis',
    component: Analysis,
    meta: { title: '岗位分析' }
  },
  {
    path: '/',
    name: 'Tickets',
    component: adaptive('Tickets', Tickets, MobileTickets),
    meta: { title: '股票统计' }
  },
  {
    path: '/stcok-case',
    name: 'StockCase',
    component: adaptive('StockCase', StockCase, MobileStockCase),
    meta: { title: '股票案例' }
  },
  {
    path: '/stcok-screen',
    name: 'BigScreen',
    component: adaptive('BigScreen', Screen, MobileScreenHint),
    meta: {
      title: '股票大屏',
      isKeepAlive: true,
      hideLayout: true
    }
  },
  {
    path: '/company-screen',
    name: 'LargeScreen',
    component: adaptive('LargeScreen', companyScreen, MobileScreenHint),
    meta: {
      title: '公司大屏',
      isKeepAlive: true,
      hideLayout: true
    }
  },
  {
    path: '/stcok-action',
    name: 'StockAction',
    component: adaptive('StockAction', StockAction, MobileStockAction),
    meta: { title: '股票操作' }
  },
  {
    path: '/stcok-article',
    name: 'StockArticle',
    component: adaptive('StockArticle', StockArticle, MobileStockArticle),
    meta: { title: '李剑文章' }
  },
  {
    path: '/stcok-bar-chart',
    name: 'StockBarChart',
    component: adaptive('StockBarChart', StockBarChart, MobileBarChart),
    meta: { title: '柱状图示例' }
  },
  {
    path: '/stcok-compounding',
    name: 'StockCompounding',
    component: adaptive('StockCompounding', StockCompounding, MobileStockCompounding),
    meta: { title: '复利力量' }
  },
  {
    path: '/stcok-fortune',
    name: 'StockFortune',
    component: adaptive('StockFortune', StockFortune, MobileStockFortune),
    meta: { title: '算命复利' }
  },

  // 兜底：未匹配路由
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 })
})

// 登录 + 权限守卫
setupRouterGuard(router)

export default router
